/**
 * The product catalogue an outlet, a mitr or a dealer orders from (owner,
 * 7 Sep 2026, rebuilt 9 Sep 2026 to the office's own list).
 *
 * The order is asked in the way the office thinks: who it is for, then the
 * body class — L3 or L5 — then the model within it, then the battery, then
 * the colour. The motor is not asked at all any more: the build decides it.
 *
 * Shared so the order form and, later, the factory intake speak the same
 * words. Labels are what the office says; keys are stable.
 */
import { vehicleClassFor, type VehicleClass } from './sale.js';

export type CatalogueBody = 'L3' | 'L5';

export const CATALOGUE_BODIES: readonly CatalogueBody[] = ['L3', 'L5'];

export interface CatalogueModel {
  key: string;
  label: string;
  body: CatalogueBody;
  /** Built to order: the form takes a note of what is wanted. */
  customisable?: boolean;
  /**
   * A cargo body. The catalogue files these under L3 by their chassis, but the
   * office sells, prices and insures them as cargo — the Jumbo included: its
   * dispatch variant is "Cargo Jumbo" and production counts it on the Cargo
   * chassis (28 Sep 2026).
   */
  cargo?: boolean;
}

export const CATALOGUE_MODELS: readonly CatalogueModel[] = [
  // L3 — the three-wheeler passenger and small cargo bodies.
  { key: 'CC_SUPER_ECO', label: 'CC Super Eco', body: 'L3' },
  { key: 'CC_SUPER_DLX', label: 'CC Super DLX', body: 'L3' },
  { key: 'CC_JUMBO', label: 'CC Jumbo', body: 'L3', cargo: true },
  { key: 'CC_MINI_CARGO', label: 'CC Mini Cargo', body: 'L3', cargo: true },
  // Owner, 1 Oct 2026: "in order form under cargo add category dust cart". The
  // dispatch form already builds it as a variant on the City Cab Cargo chassis.
  { key: 'CC_DUST_CART', label: 'CC Dust Cart', body: 'L3', cargo: true },
  { key: 'CC_CARGO_CUSTOMISED', label: 'CC Cargo Customised', body: 'L3', customisable: true, cargo: true },
  { key: 'CC_CARGO_SUPER_CUSTOMISED', label: 'CC Cargo Super Customised', body: 'L3', customisable: true, cargo: true },
  // L5 — the larger builds.
  // Offered to every counter since 21 Sep 2026 (owner: "remove the rule e max
  // eco sold to dealers only"); it had been dealers-only from 9 Sep.
  { key: 'CC_EMAX_ECO', label: 'CC E-Max Eco', body: 'L5' },
  { key: 'CC_EMAX_DLX', label: 'CC E-Max DLX', body: 'L5' },
  { key: 'CC_PRO', label: 'CC Pro', body: 'L5', customisable: true },
];

export const BATTERY_TYPES = ['Lead acid', 'Lithium-ion'] as const;
export type BatteryType = (typeof BATTERY_TYPES)[number];

/**
 * What each chemistry actually comes in (owner, 9 Sep 2026; lead acid's 100
 * and 102 Ah added 1 Oct 2026: "in battery also add options of 100ah and 102ah
 * in lead acid").
 */
export const BATTERY_AH_BY_TYPE: Record<BatteryType, readonly number[]> = {
  'Lead acid': [100, 102, 140, 150],
  'Lithium-ion': [105, 107, 132, 140, 150, 232],
};

export const CATALOGUE_COLOURS = [
  'White',
  'Red',
  'Sky blue',
  'Black',
  'Army green',
  'Golden',
  'Pink',
] as const;

/**
 * Some vehicles are ordered with no battery at all — the buyer fits their
 * own (owner, 9 Sep 2026: "some vehicle orders also come without battery").
 * It is a battery choice, not a chemistry, so it carries no size.
 */
export const BATTERY_NONE = 'Without battery';
export type BatteryChoice = BatteryType | typeof BATTERY_NONE;

export function isBatteryChosen(choice: string | null | undefined): choice is BatteryType {
  return Boolean(choice) && choice !== BATTERY_NONE;
}

/**
 * The battery choices a body comes in. The L5 builds are lithium only
 * (owner, 9 Sep 2026: "remove lead acid from L5"); either body may be
 * ordered bare.
 *
 * `leadByException` puts lead acid back on an L5 for one order the office
 * chooses to make (owner, 18 Oct 2026: "Can you make and exception i need to
 * send only one lead L5"). The rule stands; the exception is the caller's to
 * grant — the order form grants it to the owner's own desks, and to an order
 * that is already an L5 on lead acid.
 */
export function batteryTypesFor(body: CatalogueBody, options: { leadByException?: boolean } = {}): readonly BatteryChoice[] {
  if (body !== 'L5' || options.leadByException) return [...BATTERY_TYPES, BATTERY_NONE] as const;
  return [...BATTERY_TYPES.filter((type) => type !== 'Lead acid'), BATTERY_NONE] as const;
}

/** The models offered for a body — the same list to every kind of buyer. */
export function modelsFor(body: CatalogueBody): CatalogueModel[] {
  return CATALOGUE_MODELS.filter((model) => model.body === body);
}

/**
 * An order's colours, one per vehicle (owner, 9 Sep 2026: "colour can be
 * different for every vehicle — if the person selects 2 quantity then it
 * should ask for the colour of the second vehicle"). They travel as one
 * comma-separated line in the order's colour field, in vehicle order, so
 * every screen that already reads that field keeps working.
 */
export function joinColours(colours: readonly string[]): string {
  return colours.filter(Boolean).join(', ');
}

export function splitColours(text: string | null | undefined): string[] {
  return (text ?? '')
    .split(',')
    .map((colour) => colour.trim())
    .filter(Boolean);
}

/** "2 × White · 1 × Red", or just "White" when they all match. */
export function summariseColours(text: string | null | undefined): string {
  const colours = splitColours(text);
  if (colours.length === 0) return '';
  const counted = new Map<string, number>();
  for (const colour of colours) counted.set(colour, (counted.get(colour) ?? 0) + 1);
  if (counted.size === 1) return colours[0]!;
  return [...counted.entries()].map(([colour, count]) => `${count} × ${colour}`).join(' · ');
}

/** "CC E-Max DLX (L5)" — the model as the order line writes it. */
export function catalogueModelLabel(model: CatalogueModel): string {
  return `${model.label} (${model.body})`;
}

// ---------------------------------------------------------------------------
// Reading an order back into the choices that made it
// ---------------------------------------------------------------------------
//
// The order stores what it asked for as words — "CC E-Max DLX (L5)",
// "Lithium-ion 105 Ah" — because that is what the office and the sheet read.
// Editing one (owner, 10 Sep 2026: "add option for editing orders for Manish
// and Goswami") means turning those words back into the taps that made them.
// One implementation, so the form that writes and the form that re-opens can
// never drift apart.

/** The model an order line names, or null when the catalogue has changed under it. */
export function modelFromLabel(label: string | null | undefined): CatalogueModel | null {
  const wanted = (label ?? '').trim().toLowerCase();
  if (!wanted) return null;
  return (
    CATALOGUE_MODELS.find((model) => catalogueModelLabel(model).toLowerCase() === wanted) ??
    CATALOGUE_MODELS.find((model) => model.label.toLowerCase() === wanted) ??
    null
  );
}

/**
 * The three kinds the order list is filtered by (owner, 26 Sep 2026: "add one
 * more filter in orders L3, L5 and cargo"), in his order.
 */
export const ORDER_CLASSES: readonly VehicleClass[] = ['L3', 'L5', 'CARGO'];
export const ORDER_CLASS_LABELS: Record<VehicleClass, string> = { L3: 'L3', L5: 'L5', CARGO: 'Cargo' };

/**
 * Which of the three an order line is — the same three the office prices and
 * insures by (`vehicleClassFor`). A catalogue model says so itself: its cargo
 * flag, else its body. The four cargo bodies — the Jumbo, the Mini Cargo and
 * both Customised builds — are Cargo though their lines read "(L3)"; the tag
 * names the chassis, not what the office sells. A spelling the catalogue no
 * longer knows is cargo when its name says so, else goes by its own "(L3)" or
 * "(L5)" tag, then by the shared rule, never by a second list here.
 */
export function orderClassFor(label: string | null | undefined): VehicleClass {
  const text = (label ?? '').trim();
  const model = modelFromLabel(text);
  if (model) return model.cargo ? 'CARGO' : model.body;
  if (vehicleClassFor(text) === 'CARGO' || /\bJUMBO\b/i.test(text)) return 'CARGO';
  const tagged = /\((L3|L5)\)/i.exec(text);
  if (tagged) return tagged[1]!.toUpperCase() as VehicleClass;
  return vehicleClassFor(text);
}

/** "Lithium-ion 105 Ah" → the chemistry and the size. "Without battery" → no size. */
export function parseBatterySpec(text: string | null | undefined): { type: BatteryChoice | null; ah: number | null } {
  const spec = (text ?? '').trim();
  if (!spec) return { type: null, ah: null };
  if (spec.toLowerCase() === BATTERY_NONE.toLowerCase()) return { type: BATTERY_NONE, ah: null };
  const type = BATTERY_TYPES.find((candidate) => spec.toLowerCase().startsWith(candidate.toLowerCase())) ?? null;
  const size = /(\d+)\s*Ah/i.exec(spec);
  const ah = size ? Number(size[1]) : null;
  if (!type) return { type: null, ah: null };
  // A size the chemistry does not come in is not a size to re-offer.
  return { type, ah: ah !== null && BATTERY_AH_BY_TYPE[type].includes(ah) ? ah : null };
}

/**
 * The note an order carries, split back into the customisation the form asked
 * for and whatever else was typed. The form writes the customisation as its
 * own first line, so it comes back off the front.
 */
export function parseOrderNote(message: string | null | undefined): { customisation: string; note: string } {
  const lines = (message ?? '').split('\n');
  const first = lines[0] ?? '';
  const match = /^Customisation:\s*(.*)$/i.exec(first.trim());
  if (!match) return { customisation: '', note: (message ?? '').trim() };
  return { customisation: match[1]!.trim(), note: lines.slice(1).join('\n').trim() };
}

// ---------------------------------------------------------------------------
// Switching a model off and on
// ---------------------------------------------------------------------------
//
// Owner, 24 Sep 2026: "Add a toggle for switching vehicle model off and on".
// The office decides what is on offer without a code change: a model the
// network can order, a vehicle type on the dispatch form, or one trim of a
// vehicle type (the Pro's plus trims were the case that asked for it).
//
// Switching off only stops a model being OFFERED. Nothing already on the book
// changes: an order raised for it stays, a vehicle built as it is still
// dispatched, and every form keeps showing a value it already holds.
//
// The switches are the maker's — the catalogue is Bright Autozone's — so they
// are kept in the maker's book (`Setting`, key MODEL_SWITCHES_KEY) and read by
// every book. Only what is OFF is stored, so a model added to the code later
// is on until somebody switches it off.

/** `Setting.key` in the maker's book. */
export const MODEL_SWITCHES_KEY = 'catalogue.modelSwitches';

export interface ModelSwitches {
  /** Order-form models switched off, by `CatalogueModel.key`. */
  orderModelsOff: string[];
  /** Dispatch-form vehicle types switched off, as `DISPATCH_VEHICLE_TYPES` spells them. */
  vehicleTypesOff: string[];
  /** Trims switched off per dispatch vehicle type: `{ 'CITY CAB E-MAX': ['MINI ECO Li'] }`. */
  trimsOff: Record<string, string[]>;
  /** The office's drivers list, active ones by name (5 Nov 2026) — the dispatch form's driver choices. Absent: the form's own list. */
  drivers?: string[];
  /** When and by whom they were last changed; null when never. */
  updatedAt: string | null;
  updatedBy: string | null;
}

export const NO_SWITCHES_OFF: ModelSwitches = { orderModelsOff: [], vehicleTypesOff: [], trimsOff: {}, updatedAt: null, updatedBy: null };

/** One switch flipped: which list, which name, on or off. */
export type ModelSwitchChange =
  | { kind: 'orderModel'; key: string; on: boolean }
  | { kind: 'vehicleType'; vehicleType: string; on: boolean }
  | { kind: 'trim'; vehicleType: string; trim: string; on: boolean };

/** Whatever is stored, read safely: unknown shapes and non-strings are dropped. */
export function readModelSwitches(value: unknown): ModelSwitches {
  const v = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const strings = (x: unknown) => (Array.isArray(x) ? [...new Set(x.filter((s): s is string => typeof s === 'string' && s.trim() !== ''))] : []);
  const trims: Record<string, string[]> = {};
  if (v.trimsOff && typeof v.trimsOff === 'object') {
    for (const [type, list] of Object.entries(v.trimsOff as Record<string, unknown>)) {
      const off = strings(list);
      if (off.length) trims[type] = off;
    }
  }
  return {
    orderModelsOff: strings(v.orderModelsOff),
    vehicleTypesOff: strings(v.vehicleTypesOff),
    trimsOff: trims,
    // Served beside the switches, never stored with them (5 Nov 2026).
    ...(Array.isArray(v.drivers) ? { drivers: strings(v.drivers) } : {}),
    updatedAt: typeof v.updatedAt === 'string' ? v.updatedAt : null,
    updatedBy: typeof v.updatedBy === 'string' ? v.updatedBy : null,
  };
}

/** The switches after one flip (pure; the caller stamps updatedAt/updatedBy). */
export function applyModelSwitch(current: ModelSwitches, change: ModelSwitchChange): ModelSwitches {
  const flip = (list: string[], name: string, on: boolean) => (on ? list.filter((x) => x !== name) : [...new Set([...list, name])]);
  if (change.kind === 'orderModel') return { ...current, orderModelsOff: flip(current.orderModelsOff, change.key, change.on) };
  if (change.kind === 'vehicleType') return { ...current, vehicleTypesOff: flip(current.vehicleTypesOff, change.vehicleType, change.on) };
  const trimsOff = { ...current.trimsOff };
  const next = flip(trimsOff[change.vehicleType] ?? [], change.trim, change.on);
  if (next.length) trimsOff[change.vehicleType] = next;
  else delete trimsOff[change.vehicleType];
  return { ...current, trimsOff };
}

/** Is this order-form model switched on? */
export function isOrderModelOn(model: Pick<CatalogueModel, 'key'>, switches: ModelSwitches | null | undefined): boolean {
  return !(switches?.orderModelsOff ?? []).includes(model.key);
}

/** The models a body offers right now — `modelsFor` less what is switched off. */
export function modelsOnFor(body: CatalogueBody, switches: ModelSwitches | null | undefined): CatalogueModel[] {
  return modelsFor(body).filter((model) => isOrderModelOn(model, switches));
}

/**
 * The switched-off model an order line names, or null when it names none. The
 * order stores the model as its label ("CC Pro (L5)"), so this reads it back
 * the way `modelFromLabel` does.
 */
export function switchedOffOrderModel(label: string | null | undefined, switches: ModelSwitches | null | undefined): CatalogueModel | null {
  const model = modelFromLabel(label);
  return model && !isOrderModelOn(model, switches) ? model : null;
}
